import { Text, Section, Grid, Column } from '@acast/decibel';
import { getShowDescription, RSS_SUBTITLE_LIMIT } from 'ducks/shows';
import get from 'lodash.get';
import T from 'prop-types';
import React from 'react';
import DescriptionAttachable from 'shared/components/description-attachable';
import createRenderToggleDisabled from 'shared/components/description-attachable/create-render-toggle-disabled';
import convertToRssSubtitle from 'shared/helpers/convert-to-rss-subtitle';
import removeHtmlTags from 'shared/helpers/remove-html-tags';
import TextField from './fields/text';
import TextAreaField from './fields/textarea';

const SettingsGroupRSS = ({ show, onChange, metaFunc }) => {
  const fieldProps = propName => ({
    propName,
    meta: metaFunc(propName),
    onChange: v => onChange(propName, v),
    value: get(show, propName),
  });

  const descriptionFieldProps = (propName, description) => ({
    value: get(show, propName),
    description,
    setValue: v => onChange(propName, v),
  });

  const renderRssSubtitle = (
    <DescriptionAttachable
      {...descriptionFieldProps('rss.subtitle', convertToRssSubtitle(getShowDescription(show)))}
    >
      {({ isAttachedToDescription, attachToDescription, detachFromDescription }) => (
        <TextAreaField
          {...fieldProps('rss.subtitle')}
          label="Subtitle"
          placeholder="Something to go with the title."
          help="This is automatically generated from the show description above and can then be edited. This is shown in the 'description' column in Apple Podcasts and is limited to 255 characters."
          subtitle={`${RSS_SUBTITLE_LIMIT - get(show, 'rss.subtitle').length} characters left`}
          disabled={isAttachedToDescription}
          renderToggleDisabled={createRenderToggleDisabled(
            attachToDescription,
            detachFromDescription
          )}
          isRequired
        />
      )}
    </DescriptionAttachable>
  );

  const renderRssSummary = (
    <DescriptionAttachable
      {...descriptionFieldProps('rss.summary', removeHtmlTags(getShowDescription(show)))}
    >
      {({ isAttachedToDescription, attachToDescription, detachFromDescription }) => (
        <TextAreaField
          {...fieldProps('rss.summary')}
          label="Summary"
          placeholder="A small summary of the contents of this show."
          help="This is automatically generated from the show description above and can then be edited."
          disabled={isAttachedToDescription}
          renderToggleDisabled={createRenderToggleDisabled(
            attachToDescription,
            detachFromDescription
          )}
          isRequired
        />
      )}
    </DescriptionAttachable>
  );

  return (
    <Section>
      <Text variant="h3">RSS Settings</Text>
      <Grid>
        <Column>{renderRssSubtitle}</Column>
        <Column>{renderRssSummary}</Column>
        <Column>
          <TextField
            {...fieldProps('rss.author')}
            label="Author"
            help="The name of the show creator/creators e.g. the creator's given name, production company or media organisation."
            placeholder="Name of the publisher."
            isRequired
          />
        </Column>
        <Column>
          <TextField
            {...fieldProps('rss.ownerEmail')}
            label="Email"
            help="The e-mail address of the show owner. This should preferably be the same e-mail address that is connected to the Apple ID used when submitting the feed to Apple Podcasts."
            placeholder="some.email@domain.com"
            isRequired
          />
        </Column>
        <Column>
          <TextField {...fieldProps('copyright')} label="Copyright" />
        </Column>
      </Grid>
    </Section>
  );
};

SettingsGroupRSS.propTypes = {
  show: T.object,
  onChange: T.func,
  metaFunc: T.func,
};

export default SettingsGroupRSS;
